import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import Select from '../../../components/ui/Select';
import Textarea from '../../../components/ui/Textarea';

function createEmptyBinding() {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    mcpServerId: '',
    instructions: '',
    contactScoped: false,
  };
}

function McpServerBindingsEditor({
  id,
  label = 'Servidores MCP',
  hint = 'Vincule ferramentas externas e descreva quando e como o sub-agente deve usá-las.',
  mcpServers = [],
  bindings = [],
  onChange,
  onRequestImprove,
  improvingBindingId = null,
  isImproveDisabled = false,
  getImproveError,
  error,
  allowContactScoped = true,
  loadError = '',
  notice = '',
}) {
  const usedIds = new Set(
    bindings.map((binding) => binding.mcpServerId).filter(Boolean)
  );

  const handleAdd = () => {
    onChange([...bindings, createEmptyBinding()]);
  };

  const handleRemove = (bindingId) => {
    onChange(bindings.filter((binding) => binding.id !== bindingId));
  };

  const handleUpdate = (bindingId, patch) => {
    onChange(
      bindings.map((binding) =>
        binding.id === bindingId ? { ...binding, ...patch } : binding
      )
    );
  };

  const availableForSelect = (currentId) =>
    mcpServers.filter(
      (server) => server.id === currentId || !usedIds.has(server.id)
    );

  const handleImproveClick = (binding) => {
    if (!onRequestImprove) {
      return;
    }

    const draft = binding.instructions?.trim();
    if (!draft) {
      return;
    }

    if (!binding.mcpServerId) {
      return;
    }

    onRequestImprove(binding);
  };

  return (
    <div>
      {label && (
        <p className="block text-sm font-medium text-slate-700 mb-2">{label}</p>
      )}

      {loadError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-700">{loadError}</p>
        </div>
      ) : mcpServers.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-3">
          <p className="text-sm text-content-muted">
            {notice || (
              <>
                Nenhum servidor MCP ativo disponível.{' '}
                <Link to="/mcp-servers/new" className="text-forge-600 hover:underline">
                  Cadastre um servidor
                </Link>{' '}
                para vincular ferramentas a este comportamento.
              </>
            )}
          </p>
        </div>
      ) : (
        <div
          id={id}
          className={`space-y-4 ${error ? 'rounded-xl border border-red-300 p-3' : ''}`}
        >
          {bindings.length === 0 && (
            <p className="text-sm text-content-muted">
              Nenhum MCP vinculado. Adicione um servidor se este comportamento precisar de ferramentas externas.
            </p>
          )}

          {bindings.map((binding, index) => {
            const selectedServer = mcpServers.find(
              (server) => server.id === binding.mcpServerId
            );
            const options = [
              { value: '', label: 'Selecione um servidor' },
              ...availableForSelect(binding.mcpServerId).map((server) => ({
                value: server.id,
                label: server.label || server.slug,
              })),
            ];

            const improveError = getImproveError?.(binding.id);
            const isBindingImproving = improvingBindingId === binding.id;
            const isOtherBindingImproving =
              improvingBindingId !== null && improvingBindingId !== binding.id;

            return (
              <div
                key={binding.id}
                className="rounded-xl border border-surface-border bg-white p-4 space-y-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <Select
                    id={`${id}_server_${binding.id}`}
                    label={`Servidor MCP ${index + 1}`}
                    value={binding.mcpServerId}
                    onChange={(e) =>
                      handleUpdate(binding.id, { mcpServerId: e.target.value })
                    }
                    options={options}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemove(binding.id)}
                    className="mt-7 p-2 text-content-muted hover:text-red-600 transition-colors"
                    aria-label="Remover MCP"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {selectedServer?.edge_catalog_status === 'missing' && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                    <p className="text-xs text-red-700">
                      Este MCP está atribuído ao Edge, mas o connector ainda não
                      publicou o catálogo. As ferramentas não estarão disponíveis
                      para este comportamento.
                    </p>
                  </div>
                )}

                <Textarea
                  id={`${id}_instructions_${binding.id}`}
                  label="Instruções de uso"
                  value={binding.instructions}
                  onChange={(e) =>
                    handleUpdate(binding.id, { instructions: e.target.value })
                  }
                  placeholder="Descreva quando e como usar as ferramentas deste servidor..."
                  rows={3}
                  hint="Obrigatório ao vincular um MCP. Use o ícone de IA no canto do campo para refinar as instruções de uso."
                  action={
                    onRequestImprove
                      ? {
                          iconOnly: true,
                          ariaLabel: 'Melhorar instruções de uso do MCP com IA',
                          loading: isBindingImproving,
                          disabled:
                            isImproveDisabled ||
                            isOtherBindingImproving ||
                            !binding.instructions?.trim() ||
                            !binding.mcpServerId,
                          onClick: () => handleImproveClick(binding),
                          error: improveError,
                        }
                      : undefined
                  }
                />
                {allowContactScoped && (
                  <label className="flex items-start gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={Boolean(binding.contactScoped)}
                      onChange={(e) =>
                        handleUpdate(binding.id, { contactScoped: e.target.checked })
                      }
                      className="mt-0.5 rounded border-slate-300"
                    />
                    <span>
                      Usar a conexão deste contato
                      <span className="block text-xs text-content-muted">
                        As ferramentas só ficam disponíveis após a pessoa autorizar o
                        aplicativo no espaço privado.
                      </span>
                    </span>
                  </label>
                )}
              </div>
            );
          })}

          <button
            type="button"
            onClick={handleAdd}
            disabled={bindings.length >= mcpServers.length}
            className="btn-secondary text-sm"
          >
            <Plus className="h-4 w-4 mr-1.5 inline" />
            Adicionar MCP
          </button>
        </div>
      )}

      {notice && mcpServers.length > 0 && !loadError && (
        <p className="mt-1.5 text-xs text-amber-700">{notice}</p>
      )}
      {hint && !error && (
        <p className="mt-1.5 text-xs text-content-muted">{hint}</p>
      )}
      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
    </div>
  );
}

export default McpServerBindingsEditor;
